import { ChangeDetectionStrategy, Component, inject, input } from '@angular/core';
import { MiseAJourApplication } from '@stockflow/offline-core';
import { LucideAngularModule, RefreshCw, X, Check } from 'lucide-angular';
import { TranslationService } from '../../../core/services/translation.service';

/** Hauteur de la bannière, en px : le layout decale le contenu d'autant pour ne rien recouvrir. */
export const HAUTEUR_BANNIERE_MAJ = 68;

/**
 * « Nouvelle version disponible » — maquette « Bannière de mise à jour », ecrans 1, 2 et 3.
 *
 *  - `disponible`   : bandeau sous l'en-tete. Le bouton est desactive (« En attente ») tant qu'une vente part :
 *                     l'application ne se recharge jamais au milieu d'un encaissement.
 *  - `rechargement` : feuille du bas, quand le navigateur ne sait plus servir la version en cache.
 *
 * Aucun texte en dur : tout vient des traductions (`pwa.update_*`, `pwa.reload_*`).
 */
@Component({
  selector: 'app-banniere-mise-a-jour',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [LucideAngularModule],
  template: `
    @switch (maj.etat()) {
      @case ('disponible') {
        <div role="status" aria-live="polite"
             class="fixed left-0 right-0 z-30 flex items-center gap-3 px-4 text-white bg-gradient-to-br from-primary-900 to-primary-700"
             [style.top.px]="haut()" [style.height.px]="hauteur">
          <span class="shrink-0 w-9 h-9 rounded-[10px] bg-white/15 flex items-center justify-center" aria-hidden="true">
            <lucide-icon [img]="RefreshCw" [size]="20"></lucide-icon>
          </span>
          <div class="flex-1 min-w-0">
            <div class="text-sm font-bold leading-tight">{{ t().pwa.update_title }}</div>
            <div class="text-xs text-white/85 leading-[1.3]">
              {{ maj.occupe() ? t().pwa.update_waiting_body : t().pwa.update_body }}
            </div>
          </div>
          <button type="button" (click)="appliquer()" [disabled]="maj.occupe()"
                  class="shrink-0 h-11 px-4 rounded-[10px] text-sm font-bold transition-colors
                         bg-white text-primary-800 hover:bg-primary-50
                         disabled:bg-white/15 disabled:text-white/80 disabled:border disabled:border-white/35 disabled:font-semibold">
            {{ maj.occupe() ? t().pwa.update_waiting_action : t().pwa.update_action }}
          </button>
          @if (!maj.occupe()) {
            <button type="button" (click)="maj.ecarter()" [attr.aria-label]="t().pwa.update_later"
                    class="shrink-0 w-11 h-11 -mr-2 rounded-[10px] flex items-center justify-center text-white hover:bg-white/10 transition-colors">
              <lucide-icon [img]="X" [size]="18" aria-hidden="true"></lucide-icon>
            </button>
          }
        </div>
      }
      @case ('rechargement') {
        <div class="fixed inset-0 z-[60] flex items-end bg-surface-950/60">
          <div role="alertdialog" aria-modal="true" aria-labelledby="maj-rc-titre" aria-describedby="maj-rc-texte"
               class="w-full rounded-t-3xl bg-white dark:bg-surface-900 px-5 pt-3 pb-8 shadow-[0_-20px_40px_rgba(15,23,42,0.35)]">
            <div class="w-10 h-1 rounded-sm bg-surface-300 dark:bg-surface-600 mx-auto mb-5" aria-hidden="true"></div>
            <div class="flex items-center gap-3.5">
              <div class="shrink-0 w-14 h-14 rounded-[14px] bg-accent-100 text-accent-700 flex items-center justify-center" aria-hidden="true">
                <lucide-icon [img]="RefreshCw" [size]="28"></lucide-icon>
              </div>
              <div class="min-w-0">
                <h2 id="maj-rc-titre" class="text-lg font-bold leading-tight text-surface-900 dark:text-white">{{ t().pwa.reload_title }}</h2>
                <p id="maj-rc-texte" class="mt-0.5 text-[13px] text-surface-500 dark:text-surface-400">{{ t().pwa.reload_body }}</p>
              </div>
            </div>
            <div class="mt-5 flex items-start gap-3 text-sm leading-snug text-surface-800 dark:text-surface-200">
              <span class="shrink-0 w-6 h-6 rounded-full bg-success-100 text-success-700 flex items-center justify-center" aria-hidden="true">
                <lucide-icon [img]="Check" [size]="14"></lucide-icon>
              </span>
              {{ t().pwa.reload_kept }}
            </div>
            <p class="mt-4 px-3.5 py-3 rounded-xl bg-surface-50 dark:bg-surface-800 border border-surface-200 dark:border-surface-700 text-[13px] leading-snug text-surface-600 dark:text-surface-300">
              {{ t().pwa.reload_hint }}
            </p>
            <button type="button" (click)="maj.recharger()"
                    class="mt-5 w-full h-12 rounded-xl bg-primary-600 hover:bg-primary-700 text-white text-[15px] font-bold shadow-lg shadow-primary-500/25 transition-colors">
              {{ t().pwa.reload_action }}
            </button>
          </div>
        </div>
      }
    }
  `,
})
export class BanniereMiseAJourComponent {
  protected readonly maj = inject(MiseAJourApplication);
  readonly t = inject(TranslationService).t;

  /** Distance au haut de l'ecran, en px : sous l'en-tete, et sous la barre de caisse quand elle est la. */
  readonly haut = input(56);

  protected readonly hauteur = HAUTEUR_BANNIERE_MAJ;
  protected readonly RefreshCw = RefreshCw;
  protected readonly X = X;
  protected readonly Check = Check;

  async appliquer(): Promise<void> {
    await this.maj.appliquer();
  }
}
